import React, { useEffect, useRef, useMemo } from 'react';
import { formatNumber } from '../../utils/formatters';
import { LoadingLogo } from '../LoadingLogo';

// Helper to extract SV name from party ID (e.g., "Digital-Asset-2::1220..." -> "Digital-Asset-2")
const extractSvName = (partyId) => {
  if (!partyId) return null;
  return partyId.split('::')[0];
};

// Helper to format time ago
const formatTimeAgo = (timestamp) => {
  if (!timestamp) return '—';
  const seconds = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  return `${Math.floor(seconds / 3600)}h ago`;
};

// Price Consensus Card Component
function PriceConsensusCard({ priceVotes, svNodes }) {
  const stats = useMemo(() => {
    if (!priceVotes || priceVotes.length === 0) return null;

    const prices = priceVotes
      .map(v => parseFloat(v.payload?.amuletPrice))
      .filter(p => !isNaN(p))
      .sort((a, b) => a - b);

    if (prices.length === 0) return null;

    const min = prices[0];
    const max = prices[prices.length - 1];
    const median = prices[Math.floor(prices.length / 2)];
    const spread = max > 0 ? ((max - min) / median * 100) : 0;

    // Find most recent vote
    const mostRecent = priceVotes.reduce((latest, vote) => {
      const voteTime = new Date(vote.payload?.lastUpdatedAt || 0).getTime();
      return voteTime > latest.time ? { time: voteTime, vote } : latest;
    }, { time: 0, vote: null });

    return {
      median,
      min,
      max,
      spread,
      voteCount: priceVotes.length,
      totalSvs: svNodes?.length || 13,
      lastVoteTime: mostRecent.vote?.payload?.lastUpdatedAt,
      lastVoteSv: extractSvName(mostRecent.vote?.payload?.sv),
    };
  }, [priceVotes, svNodes]);

  if (!stats) return null;

  return (
    <div style={{
      background: 'var(--bg-elevated)',
      border: '1px solid var(--border-default)',
      borderRadius: '8px',
      padding: '24px',
      marginBottom: '24px',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '20px',
      }}>
        <span style={{ fontSize: '18px', opacity: 0.6 }}>⚖️</span>
        <h3 style={{
          fontSize: '16px',
          fontWeight: '600',
          color: 'var(--text-primary)',
          margin: 0,
        }}>Price Consensus</h3>
        <span style={{
          marginLeft: 'auto',
          fontSize: '11px',
          color: 'var(--text-tertiary)',
          background: 'var(--bg-subtle)',
          padding: '4px 10px',
          borderRadius: '12px',
        }}>
          {stats.voteCount}/{stats.totalSvs} votes
        </span>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '12px',
      }}>
        {/* Median Price */}
        <div style={{
          background: 'var(--bg-subtle)',
          borderRadius: '8px',
          padding: '16px',
          textAlign: 'center',
        }}>
          <div style={{
            fontSize: '10px',
            color: 'var(--text-disabled)',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            marginBottom: '6px',
          }}>Median Price</div>
          <div style={{
            fontSize: '22px',
            fontWeight: '700',
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-mono)',
          }}>
            ${stats.median.toFixed(6)}
          </div>
        </div>

        {/* Price Range */}
        <div style={{
          background: 'var(--bg-subtle)',
          borderRadius: '8px',
          padding: '16px',
          textAlign: 'center',
        }}>
          <div style={{
            fontSize: '10px',
            color: 'var(--text-disabled)',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            marginBottom: '6px',
          }}>Range</div>
          <div style={{
            fontSize: '11px',
            color: 'var(--text-secondary)',
            fontFamily: 'var(--font-mono)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '2px',
          }}>
            <span>${stats.min.toFixed(6)}</span>
            <span style={{ color: 'var(--text-disabled)', fontSize: '9px' }}>↓</span>
            <span>${stats.max.toFixed(6)}</span>
          </div>
        </div>

        {/* Spread */}
        <div style={{
          background: 'var(--bg-subtle)',
          borderRadius: '8px',
          padding: '16px',
          textAlign: 'center',
        }}>
          <div style={{
            fontSize: '10px',
            color: 'var(--text-disabled)',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            marginBottom: '6px',
          }}>Spread</div>
          <div style={{
            fontSize: '22px',
            fontWeight: '700',
            color: 'var(--text-secondary)',
            fontFamily: 'var(--font-mono)',
          }}>
            {stats.spread.toFixed(2)}%
          </div>
        </div>

        {/* Last Vote */}
        <div style={{
          background: 'var(--bg-subtle)',
          borderRadius: '8px',
          padding: '16px',
          textAlign: 'center',
        }}>
          <div style={{
            fontSize: '10px',
            color: 'var(--text-disabled)',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            marginBottom: '6px',
          }}>Last Vote</div>
          <div style={{
            fontSize: '13px',
            color: 'var(--text-primary)',
          }}>
            {stats.lastVoteSv?.split('-').slice(0, 2).join('-') || '—'}
          </div>
          <div style={{
            fontSize: '11px',
            color: 'var(--text-disabled)',
            marginTop: '2px',
          }}>
            {formatTimeAgo(stats.lastVoteTime)}
          </div>
        </div>
      </div>
    </div>
  );
}

export function SuperValidatorsTab({ data, loading, highlightParty }) {
  const highlightRef = useRef(null);

  // Create a map of SV name -> price vote for quick lookup
  const priceVoteMap = useMemo(() => {
    const map = new Map();
    const votes = data.networkStats?.amuletPriceVotes || [];
    votes.forEach(vote => {
      const svName = extractSvName(vote.payload?.sv);
      if (svName) {
        map.set(svName, {
          price: parseFloat(vote.payload?.amuletPrice),
          lastUpdatedAt: vote.payload?.lastUpdatedAt,
        });
      }
    });
    return map;
  }, [data.networkStats?.amuletPriceVotes]);

  // Calculate median for comparison
  const medianPrice = useMemo(() => {
    const prices = Array.from(priceVoteMap.values())
      .map(v => v.price)
      .filter(p => !isNaN(p))
      .sort((a, b) => a - b);
    return prices.length > 0 ? prices[Math.floor(prices.length / 2)] : null;
  }, [priceVoteMap]);

  // Scroll to highlighted row when component mounts or highlightParty changes
  useEffect(() => {
    if (highlightParty && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightParty]);

  const priceVotes = data.networkStats?.amuletPriceVotes || [];

  return (
    <div>
      {/* Price Consensus Card */}
      <PriceConsensusCard priceVotes={priceVotes} svNodes={data.svNodes} />

      {/* SV Table */}
      <section style={{
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border-default)',
        borderRadius: '8px',
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid var(--border-default)',
        }}>
          <h2 style={{
            fontSize: '20px',
            fontWeight: '600',
            color: 'var(--text-primary)',
            marginBottom: '4px',
          }}>Super Validators</h2>
          <p style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>
            Governing nodes with voting power on the Canton Network
          </p>
        </div>

        {/* Desktop header */}
        <div className="hide-mobile" style={{
          display: 'grid',
          gridTemplateColumns: '50px minmax(0, 1.5fr) 120px 90px 100px',
          gap: '12px',
          padding: '12px 24px',
          background: 'var(--bg-subtle)',
          fontSize: '11px',
          color: 'var(--text-disabled)',
          textTransform: 'uppercase',
          letterSpacing: '1px',
        }}>
          <div>Rank</div>
          <div>SV Node</div>
          <div style={{ textAlign: 'right' }}>Price Vote</div>
          <div style={{ textAlign: 'right' }}>Last Vote</div>
          <div style={{ textAlign: 'right' }}>Weight</div>
        </div>
        {/* Mobile header */}
        <div className="show-mobile-only" style={{
          display: 'flex',
          padding: '10px 12px',
          background: 'var(--bg-subtle)',
          fontSize: '9px',
          color: 'var(--text-disabled)',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          alignItems: 'center',
        }}>
          <div style={{ width: '30px', flexShrink: 0 }}>#</div>
          <div style={{ flex: 1 }}>Node</div>
          <div style={{ width: '75px', textAlign: 'right', flexShrink: 0 }}>Price</div>
          <div style={{ width: '45px', textAlign: 'right', flexShrink: 0 }}>Vote</div>
        </div>

        <div style={{ maxHeight: 'calc(100vh - 450px)', overflow: 'auto' }}>
          {data.svNodes.length > 0 ? (
            data.svNodes.map((sv, i) => {
              const isHighlighted = highlightParty && sv.party === highlightParty;
              const vote = priceVoteMap.get(sv.name);
              const priceDiff = vote && medianPrice ? ((vote.price - medianPrice) / medianPrice * 100) : 0;
              const isOffMedian = Math.abs(priceDiff) > 0.01;

              return (
                <div key={sv.party || i} ref={isHighlighted ? highlightRef : null}>
                  {/* Desktop row */}
                  <div
                    className="hide-mobile"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '50px minmax(0, 1.5fr) 120px 90px 100px',
                      gap: '12px',
                      padding: '14px 24px',
                      borderBottom: '1px solid var(--border-subtle)',
                      alignItems: 'center',
                      transition: 'background 0.15s ease',
                      background: isHighlighted ? 'var(--accent-muted)' : 'transparent',
                    }}
                    onMouseEnter={(e) => { if (!isHighlighted) e.currentTarget.style.background = 'rgba(255, 255, 255, 0.02)'; }}
                    onMouseLeave={(e) => { if (!isHighlighted) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <div style={{
                      fontSize: '14px',
                      color: i < 3 ? 'var(--text-primary)' : 'var(--text-tertiary)',
                      fontFamily: 'var(--font-mono)',
                      fontWeight: i < 3 ? '700' : '400',
                    }}>#{i + 1}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                      <div style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: 'var(--text-primary)',
                        flexShrink: 0,
                      }} />
                      <span style={{
                        color: 'var(--text-primary)',
                        fontSize: '14px',
                        fontWeight: '600',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>
                        {sv.name}
                      </span>
                    </div>
                    <div style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: '13px',
                      textAlign: 'right',
                      color: isOffMedian ? 'var(--text-tertiary)' : 'var(--text-secondary)',
                      fontWeight: '600',
                    }}>
                      {vote ? `$${vote.price.toFixed(6)}` : '—'}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: 'var(--text-disabled)',
                      textAlign: 'right',
                    }}>
                      {vote ? formatTimeAgo(vote.lastUpdatedAt) : '—'}
                    </div>
                    <div style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: '14px',
                      color: 'var(--text-secondary)',
                      textAlign: 'right',
                      fontWeight: '600',
                    }}>{formatNumber(sv.weight, 0)}</div>
                  </div>
                  {/* Mobile row */}
                  <div
                    className="show-mobile-only"
                    style={{
                      display: 'flex',
                      padding: '12px',
                      borderBottom: '1px solid var(--border-subtle)',
                      alignItems: 'center',
                      background: isHighlighted ? 'var(--accent-muted)' : 'transparent',
                    }}
                  >
                    <div style={{
                      width: '30px',
                      flexShrink: 0,
                      fontSize: '12px',
                      color: i < 3 ? 'var(--text-primary)' : 'var(--text-tertiary)',
                      fontFamily: 'var(--font-mono)',
                      fontWeight: i < 3 ? '700' : '400',
                    }}>#{i + 1}</div>
                    <div style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      minWidth: 0,
                    }}>
                      <div style={{
                        width: '5px',
                        height: '5px',
                        borderRadius: '50%',
                        background: 'var(--text-primary)',
                        flexShrink: 0,
                      }} />
                      <span style={{
                        color: 'var(--text-primary)',
                        fontSize: '11px',
                        fontWeight: '600',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>
                        {sv.name}
                      </span>
                    </div>
                    <div style={{
                      width: '75px',
                      flexShrink: 0,
                      fontFamily: 'var(--font-mono)',
                      fontSize: '10px',
                      textAlign: 'right',
                      color: isOffMedian ? 'var(--text-tertiary)' : 'var(--text-secondary)',
                      fontWeight: '600',
                    }}>
                      {vote ? `$${vote.price.toFixed(6)}` : '—'}
                    </div>
                    <div style={{
                      width: '45px',
                      flexShrink: 0,
                      fontSize: '9px',
                      color: 'var(--text-disabled)',
                      textAlign: 'right',
                    }}>
                      {vote ? formatTimeAgo(vote.lastUpdatedAt).replace(' ago', '') : '—'}
                    </div>
                  </div>
                </div>
              );
            })
          ) : loading ? (
            <LoadingLogo size={40} />
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-tertiary)' }}>
              No super validators found
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
